
// Load jQuery
import $ from 'jquery';
const jqueryInstance = $;
window.$ = jqueryInstance;
window.jQuery = jqueryInstance;
globalThis.$ = jqueryInstance;
globalThis.jQuery = jqueryInstance;

// Load Bootstrap's JavaScript
import * as bootstrap from 'bootstrap';
window.bootstrap = bootstrap;

/**
 * Normaliza atributos legacy de Bootstrap 4 a su equivalente en Bootstrap 5.
 *
 * @author Sergio Vargas
 * @returns {void}
 */
function normalizeLegacyBootstrapDataAttributes() {
    const legacyAttributeMap = {
        'data-toggle': 'data-bs-toggle',
        'data-target': 'data-bs-target',
        'data-dismiss': 'data-bs-dismiss',
        'data-placement': 'data-bs-placement',
        'data-trigger': 'data-bs-trigger',
        'data-html': 'data-bs-html',
    };

    Object.entries(legacyAttributeMap).forEach(([legacyAttribute, bootstrapFiveAttribute]) => {
        document.querySelectorAll(`[${legacyAttribute}]`).forEach((element) => {
            if (!element.hasAttribute(bootstrapFiveAttribute)) {
                element.setAttribute(bootstrapFiveAttribute, element.getAttribute(legacyAttribute) ?? '');
            }
        });
    });
}

/**
 * Obtiene o crea una instancia Bootstrap 5 para un elemento dado.
 *
 * @author Sergio Vargas
 * @param {HTMLElement} element
 * @param {typeof bootstrap.Modal | typeof bootstrap.Tooltip | typeof bootstrap.Dropdown | typeof bootstrap.Tab | typeof bootstrap.Collapse | typeof bootstrap.Alert} BootstrapComponent
 * @returns {object|null}
 */
function getOrCreateBootstrapInstance(element, BootstrapComponent) {
    if (!element || !BootstrapComponent) {
        return null;
    }

    return BootstrapComponent.getInstance(element) ?? new BootstrapComponent(element);
}

/**
 * Instala una capa mínima de compatibilidad para plugins jQuery de Bootstrap 4.
 *
 * @author Sergio Vargas
 * @returns {void}
 */
function installJQueryBootstrapBridge() {
    if (!window.$ || !window.bootstrap || !$.fn) {
        return;
    }

    /**
     * Registra un plugin jQuery que delega la acción al componente Bootstrap 5.
     *
     * @author Sergio Vargas
     * @param {string} pluginName
     * @param {Function} resolver
     * @returns {void}
     */
    function registerBootstrapPlugin(pluginName, resolver) {
        $.fn[pluginName] = function bootstrapCompatibilityBridge(...args) {
            let returnValue = this;

            this.each(function bootstrapElementIterator() {
                const resolvedValue = resolver(this, ...args);

                if (resolvedValue !== undefined && resolvedValue !== null && resolvedValue !== this) {
                    returnValue = resolvedValue;
                }
            });

            return returnValue;
        };
    }

    registerBootstrapPlugin('modal', (element, action) => {
        const modalInstance = getOrCreateBootstrapInstance(element, bootstrap.Modal);

        if (!modalInstance || typeof action !== 'string') {
            return modalInstance;
        }

        if (action === 'show' || action === 'hide' || action === 'toggle') {
            modalInstance[action]();
        }

        return modalInstance;
    });

    registerBootstrapPlugin('tooltip', (element, actionOrOptions) => {
        const tooltipInstance = getOrCreateBootstrapInstance(element, bootstrap.Tooltip);

        if (!tooltipInstance) {
            return null;
        }

        if (typeof actionOrOptions === 'string' && typeof tooltipInstance[actionOrOptions] === 'function') {
            tooltipInstance[actionOrOptions]();
        }

        return tooltipInstance;
    });

    registerBootstrapPlugin('dropdown', (element, action) => {
        const dropdownInstance = getOrCreateBootstrapInstance(element, bootstrap.Dropdown);

        if (!dropdownInstance || typeof action !== 'string') {
            return dropdownInstance;
        }

        if (action === 'show' || action === 'hide' || action === 'toggle' || action === 'update') {
            dropdownInstance[action]();
        }

        return dropdownInstance;
    });

    registerBootstrapPlugin('tab', (element, action) => {
        const tabInstance = getOrCreateBootstrapInstance(element, bootstrap.Tab);

        if (!tabInstance || typeof action !== 'string') {
            return tabInstance;
        }

        if (action === 'show') {
            tabInstance.show();
        }

        return tabInstance;
    });

    registerBootstrapPlugin('collapse', (element, action) => {
        const collapseInstance = getOrCreateBootstrapInstance(element, bootstrap.Collapse);

        if (!collapseInstance || typeof action !== 'string') {
            return collapseInstance;
        }

        if (action === 'show' || action === 'hide' || action === 'toggle') {
            collapseInstance[action]();
        }

        return collapseInstance;
    });

    registerBootstrapPlugin('alert', (element, action) => {
        const alertInstance = getOrCreateBootstrapInstance(element, bootstrap.Alert);

        if (!alertInstance || typeof action !== 'string') {
            return alertInstance;
        }

        if (action === 'close') {
            alertInstance.close();
        }

        return alertInstance;
    });
}

document.addEventListener('DOMContentLoaded', () => {
    normalizeLegacyBootstrapDataAttributes();
    installJQueryBootstrapBridge();
});

// Load FullCalendar and set up the calendar
import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es'; // Importing Spanish locale

document.addEventListener('DOMContentLoaded', function () {
    var calendarEl = document.getElementById('calendar');
    if (calendarEl) {
        var calendar = new Calendar(calendarEl, {
            plugins: [dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin],
            initialView: 'dayGridMonth',
            locale: esLocale, // Set locale to Spanish
            headerToolbar: {
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
            },
            expandRows: true,
            slotMinTime: '04:00',
            slotMaxTime: '22:00',
            eventTimeFormat: {
                hour: 'numeric',
                minute: '2-digit',
                meridiem: 'short'
            },
            displayEventEnd: true,
            editable: true,
            selectable: true,
            businessHours: true,
            dayMaxEvents: true, // allow "more" link when too many events
            events: window.calendarEvents // Use events from the global variable
        });

        calendar.render();
    }
});

// Global Search Component
import './components/global-search';
